/**
 * Dynamic Favicon Component
 * Updates favicon dynamically based on app state (notifications, theme, etc.)
 */

import { useEffect, useCallback } from 'react';
import { useNotifications } from '../../context/NotificationContext';

const DynamicFavicon = ({
  showNotification = false,
  notificationCount = 0
}) => {
  const { unreadCount } = useNotifications();
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateFavicon = useCallback(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    
    // Draw base favicon
    drawBaseFavicon(ctx);
    
    // Add notification badge if needed
    const activeNotificationCount = showNotification ? notificationCount : unreadCount;
    if (activeNotificationCount > 0) {
      drawNotificationBadge(ctx, activeNotificationCount);
    }
    
    // Update favicon in DOM
    updateFaviconInDOM(canvas);
  }, [showNotification, notificationCount, unreadCount]);
  
  useEffect(() => {
    updateFavicon();
  }, [updateFavicon]);
  
  const drawBaseFavicon = (ctx) => {
    // Clear canvas
    ctx.clearRect(0, 0, 32, 32);
    
    // Jamaica gradient background
    const gradient = ctx.createLinearGradient(0, 0, 32, 32);
    gradient.addColorStop(0, '#009639'); // Jamaica Green
    gradient.addColorStop(1, '#FFD700'); // Jamaica Gold
    
    // Draw circle
    ctx.beginPath();
    ctx.arc(16, 16, 15, 0, 2 * Math.PI);
    ctx.fillStyle = gradient;
    ctx.fill();
    
    // White border
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // "JD" text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 12px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Text shadow for better visibility
    ctx.strokeStyle = '#009639';
    ctx.lineWidth = 0.5;
    ctx.strokeText('JD', 16, 16);
    ctx.fillText('JD', 16, 16);
  };
  
  const drawNotificationBadge = (ctx, count) => {
    // Red notification circle
    ctx.beginPath();
    ctx.arc(24, 8, 6, 0, 2 * Math.PI);
    ctx.fillStyle = '#FF4444';
    ctx.fill();
    
    // White border
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Count text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 8px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const displayCount = count > 99 ? '99+' : count > 9 ? '9+' : count.toString();
    ctx.fillText(displayCount, 24, 8);
  };
  
  const updateFaviconInDOM = (canvas) => {
    // Remove existing dynamic favicon
    const existingFavicon = document.querySelector("link[rel='shortcut icon'][data-dynamic='true']");
    if (existingFavicon) {
      existingFavicon.remove();
    }
    
    // Create new favicon link
    const link = document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = canvas.toDataURL('image/x-icon');
    link.setAttribute('data-dynamic', 'true');
    
    // Add to head
    document.head.appendChild(link);
  };
  
  return null; // This component doesn't render anything
};

// Hook for easier usage
export const useDynamicFavicon = (options = {}) => {
  const { unreadCount } = useNotifications();
  
  useEffect(() => {
    // Create favicon with current options
    DynamicFavicon({
      showNotification: true,
      notificationCount: unreadCount,
      ...options
    });
    
    return () => {
      // Cleanup: restore original favicon
      const dynamicFavicon = document.querySelector("link[rel='shortcut icon'][data-dynamic='true']");
      if (dynamicFavicon) {
        dynamicFavicon.remove();
      }
    };
  }, [unreadCount, options]);
};

export default DynamicFavicon;